import { useState, useEffect } from 'react';

const usePWAInstall = () => {
  const [deferredPrompt, setDeferredPrompt] = useState(null);
  const [canInstall, setCanInstall] = useState(false);
  const [isInstalled, setIsInstalled] = useState(false);

  useEffect(() => {
    // Check if the app is already installed
    const isAppInstalled = () => {
      // For iOS
      if (window.navigator.standalone) return true;
      
      // For Android
      if (window.matchMedia('(display-mode: standalone)').matches) {
        return true;
      }
      
      // For newer browsers that support the beforeinstallprompt event
      if (window.matchMedia('(display-mode: standalone)').matches || 
          window.navigator.standalone || 
          document.referrer.includes('android-app://')) {
        return true;
      }
      
      return false;
    };

    setIsInstalled(isAppInstalled());

    // Handle beforeinstallprompt event
    const handleBeforeInstallPrompt = (e) => {
      // Prevent the default browser install prompt
      e.preventDefault();
      // Stash the event so it can be triggered later
      setDeferredPrompt(e);
      setCanInstall(true);
    };

    // Handle app installed event
    const handleAppInstalled = () => {
      console.log('App was installed');
      setCanInstall(false);
      setIsInstalled(true);
    };

    // Add event listeners
    window.addEventListener('beforeinstallprompt', handleBeforeInstallPrompt);
    window.addEventListener('appinstalled', handleAppInstalled);

    // Clean up
    return () => {
      window.removeEventListener('beforeinstallprompt', handleBeforeInstallPrompt);
      window.removeEventListener('appinstalled', handleAppInstalled);
    };
  }, []);

  // Function to trigger the installation prompt
  const install = async () => {
    if (!deferredPrompt) {
      console.log('Install prompt not available');
      return false;
    }

    try {
      // Show the install prompt
      deferredPrompt.prompt();
      
      // Wait for the user to respond to the prompt
      const { outcome } = await deferredPrompt.userChoice;
      
      // Log the result
      console.log(`User response to the install prompt: ${outcome}`);
      
      // Clear the deferred prompt
      setDeferredPrompt(null);
      
      if (outcome === 'accepted') {
        setCanInstall(false);
        setIsInstalled(true);
        return true;
      }
      
      return false;
    } catch (error) {
      console.error('Error showing install prompt:', error);
      return false;
    }
  };

  return { canInstall, isInstalled, install };
};

export default usePWAInstall;
